import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Phone, MessageCircle, ArrowRight, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MAPS_URL, BUSINESS_INFO } from "@/lib/business-info";

const SITES = {
    flexshade: {
        nombre: "FlexShade",
        tel: "+52 81 8989 9822",
        telRaw: "+528189899822",
        waRaw: "528116289267",
        wa_msg: "Hola, quiero cotizar un techo FlexShade modular.",
    },
    velarias: {
        nombre: "Skycover",
        tel: "+52 81 8989 9822",
        telRaw: "+528189899822",
        waRaw: "528116289267",
        wa_msg: "Hola, quiero cotizar mi proyecto de velaria/tensoestructura.",
    },
    eclipse: {
        nombre: "Eclipse Cubiertas",
        tel: "+52 81 8989 9822",
        telRaw: "+528189899822",
        waRaw: "528116289267",
        wa_msg: "Hola, acabo de contactar a Eclipse Cubiertas.",
    },
};

export default function Thanks() {
    const [params] = useSearchParams();
    const sitio = params.get("sitio") || "eclipse";
    const site = SITES[sitio] || SITES.eclipse;
    const waUrl =
        "https://wa.me/" +
        site.waRaw +
        "?text=" +
        encodeURIComponent(site.wa_msg);

    useEffect(() => {
        document.title = `¡Gracias! · ${site.nombre}`;
        // Block search engine indexing for conversion page
        let robotsTag = document.querySelector('meta[name="robots"]');
        if (!robotsTag) {
            robotsTag = document.createElement("meta");
            robotsTag.setAttribute("name", "robots");
            document.head.appendChild(robotsTag);
        }
        robotsTag.setAttribute("content", "noindex, nofollow");
    }, [site.nombre]);

    return (
        <div
            className="min-h-screen bg-white flex items-center justify-center px-6 py-16"
            data-testid="thanks-page"
        >
            <div className="text-center max-w-2xl">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-50 mb-8">
                    <CheckCircle2
                        className="w-14 h-14 text-green-600"
                        strokeWidth={1.75}
                    />
                </div>
                <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-4">
                    {site.nombre.toUpperCase()}
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl text-eclipse-primary mb-4">
                    ¡Gracias! Recibimos tu solicitud
                </h1>
                <p className="text-lg text-eclipse-muted mb-10 leading-relaxed">
                    Un asesor te contactará en menos de 24 horas hábiles.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
                    <a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="thanks-whatsapp-btn"
                        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider text-white bg-[#25D366] hover:bg-[#1ebd5b] transition-colors"
                    >
                        <MessageCircle className="w-5 h-5" />
                        Contactar por WhatsApp
                    </a>
                    <a
                        href={`tel:${site.telRaw}`}
                        data-testid="thanks-phone-btn"
                        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider btn-eclipse-outline-dark"
                    >
                        <Phone className="w-5 h-5" />
                        {site.tel}
                    </a>
                    <a
                        href={MAPS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="thanks-maps-btn"
                        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider border-2 border-eclipse-cta text-eclipse-cta hover:bg-eclipse-cta hover:text-white transition-colors"
                    >
                        <MapPin className="w-5 h-5" />
                        ¿Quieres venir? Cómo llegar
                        <ExternalLink className="w-4 h-4 opacity-80" />
                    </a>
                </div>

                <p className="text-xs text-eclipse-muted -mt-8 mb-12 leading-relaxed">
                    {BUSINESS_INFO.address.street}, {BUSINESS_INFO.address.detail} · C.P. {BUSINESS_INFO.address.postalCode}, {BUSINESS_INFO.address.city}, {BUSINESS_INFO.address.state}
                </p>

                <div className="border-t border-neutral-200 pt-8">
                    <p className="text-sm text-eclipse-muted mb-4">
                        ¿Mientras tanto? Explora más
                    </p>
                    <Link to="/">
                        <Button
                            variant="link"
                            data-testid="thanks-home-link"
                            className="text-eclipse-cta hover:text-eclipse-primary font-display font-bold inline-flex items-center gap-2"
                        >
                            Ver más proyectos en Eclipse Cubiertas
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
